/**
 * Contract shapes this slice reads and writes, mirrored field-for-field from
 * packages/contracts/openapi.json (schema names in the comments). They exist only until web-shell
 * commits the generated client (src/lib/api/generated); W2 replaces each with its generated twin.
 * Owner: web-case-composer (slice 16).
 */
import type { WorkOrderStatus } from '@patchbay/ui'
import type { Backend, CaseUpdateOut, DeliveryState, NativeStatus, PostSendPlan, SendSuggestions } from '../../../lib/api/generated'
import type { CaseDetail, Thread, Turn, TurnFile } from '../../../lib/types'

/** openapi: WithheldReason */
export interface WithheldReason {
  code: string
  message: string
  field?: string | null
  severity?: 'block' | 'warn'
}

export type SendBox = 'enabled' | 'withheld' | 'connect_desk' | 'mirror_stale' | 'guard_unavailable'

/** openapi: GuardVerdict */
export interface GuardVerdict {
  allowed: boolean
  phase: 'compose' | 'send'
  send_box: SendBox
  send_box_reason?: string | null
  to?: string[]
  cc?: string[]
  removed_recipients?: { address: string; reason: string }[]
  /** Counts only, never the values that were stripped. */
  scrub_report?: Record<string, number>
  scrubbed_body_html?: string | null
  withheld_reasons?: WithheldReason[]
  warnings?: WithheldReason[]
  decision_id?: string | null
  /** Warn-then-allow (round 7): codes to acknowledge. Always empty on a person's send (#227: advice, no ticks). */
  acknowledge_codes?: string[]
  /** #227: the guard did not answer (timeout, error, 409 in_progress); a person's send still goes, unchecked. */
  unchecked?: string | null
  reply_intent?: 'close' | 'ask' | 'update' | 'resolve'
  asks_customer?: boolean
}

/** The round-7 outcome when Conduit's secret list could not be read: warn, then allow a person's send. */
export const SECRET_CHECK_UNAVAILABLE = 'secret_check_unavailable'

/** openapi: ComposeCheckIn */
export interface ComposeCheckIn {
  thread_id: string
  body_html: string
  to?: string[]
  cc?: string[]
  greeting_name?: string | null
  quote_previous?: boolean | null
  inline_images?: InlineImageRef[]
}

/** openapi: SendIn */
export interface SendIn {
  thread_id: string
  body_html: string
  to: string[]
  cc?: string[]
  greeting_name?: string | null
  idempotency_key: string
  quote_previous?: boolean | null
  riley_draft_fingerprint?: string | null
  /** #390's Riley tag when the bound draft has one; the server classifies the sent bytes when absent. */
  reply_intent?: 'close' | 'closing' | 'waiting' | 'working' | 'scheduling' | null
  /** Send-phase warning codes the person read and accepted (warn-then-allow, round 7). */
  acknowledged_warnings?: string[]
  draft_intent?: 'close' | 'ask' | 'update' | 'resolve' | null
  after_send_action?: 'close' | 'wait' | null
  /** #746: the post-send chip row; applied by the server only after Desk reports the reply delivered. */
  post_send?: PostSendPlan | null
  inline_images?: InlineImageRef[]
}

export interface InlineImageRef {
  image_id: string
  filename?: string | null
  alt_text?: string | null
  width_px?: number | null
}

export type TimeDomain = 'phone' | 'it' | 'macd'

/** openapi: TimeDomainDefault (round 13). `hourly_rate`/`rate_source` arrive only for money roles. */
export interface TimeDomainDefault {
  domain: TimeDomain
  decision: 'covered' | 'billable' | 'partial' | 'unknown'
  billable_default: boolean
  basis: string
  /** Verbatim contract/decision note; omitted by role_response for techs. */
  basis_detail?: string | null
  hourly_rate?: string | number | null
  rate_source?: string | null
  currency?: string
}

/** openapi: TimeDefaultsOut: GET /cases/{id}/time-prompt (read-only). */
export interface TimeDefaultsOut {
  domain: TimeDomain
  domain_source: string
  domains: TimeDomainDefault[]
}

/** openapi: TimePromptOut */
export interface TimePromptOut {
  prompt_ref: string
  default_minutes?: number | null
  default_source?: string | null
  billable_default?: boolean | null
  billable_basis?: string | null
  domain?: TimeDomain | null
  domain_source?: string | null
  domains?: TimeDomainDefault[]
}

/** openapi: SendOut */
export interface SendOut {
  desk_thread_id: string
  /** #1492: files Desk left off the sent email; the reply is sent, attach these in Desk. */
  attachments_missing?: string[]
  sent_at: string
  via: 'sendReply' | 'sendDraft'
  riley_draft: 'sent_as_draft' | 'discarded' | 'none' | 'discard_failed'
  guard_decision_id: string
  time_prompt: TimePromptOut
  /** Whose Desk identity sent it (round 6): the person's own token, or Riley's for an approved proposal. */
  sent_as?: 'user' | 'riley'
  acknowledged_warnings?: string[]
  /** PENDING: Desk accepted the reply; delivery is still confirming (#475). Never resend it. */
  desk_status?: 'SUCCESS' | 'PENDING' | null
  /** #227: why the guard did not check this send (it went anyway); empty when it was checked. */
  guard_unchecked?: string[]
  /** #227: codes the advisory guard raised; shown, never required ticks. */
  guard_warnings?: string[]
  initiated_by?: 'human' | 'auto'
  /** #390: open the same finish_case confirmation after this send settles. */
  after_send_action?: 'close' | null
  /** #180: the proposed next move and status after this send; nothing is applied until the person submits. */
  suggestions?: SendSuggestions | null
  /** #746: the result of the chip row's choice; ok=false never means the reply failed. */
  post_send_update?: CaseUpdateOut | null
  /** #402: the per-part result of the waiting fields applied by Send & wait. */
  wait_update?: {
    ok: boolean
    parts: {
      part: string
      state: 'done' | 'already_done' | 'skipped' | 'failed' | 'pending_token' | 'unconfirmed'
      detail?: string
      code?: string | null
      retryable?: boolean
    }[]
    conversation_state?: string | null
    desk_status?: string | null
  } | null
}

/** POST /cases/{id}/notes: an Internal-tab private note under the user's own Desk token (round 6). */
export interface StaffNoteIn {
  body_text: string
  body_html?: string | null
  inline_images?: InlineImageRef[]
  desk_ticket_id?: string | null
  /** #1186: manager/cover attribution target (one of these, never both). */
  user_id?: string | null
  owner_desk_agent_id?: string | null
  idempotency_key: string
}

export interface ComposerImageOut {
  id: string
  url: string
  filename: string
  content_type: string
  size_bytes: number
  sha256: string
}

export interface StaffNoteOut {
  /** pending_token (#148): no Desk token; queued and posted under your own token when you connect Desk. */
  state?: 'posted' | 'pending_token'
  desk_ticket_id: string
  /** null while the note is queued. */
  desk_comment_id?: string | null
  /** Desk's read-back of the comment's visibility. */
  is_public?: boolean | null
  visibility_verified: boolean
  posted_at?: string | null
  queued_at?: string | null
  reconciled?: boolean
  /** Who the note @mentioned and who got a Patchbay bell / email (#113). */
  mentions?: { zuid: string; name: string; notified: boolean; emailed?: boolean }[]
}

export interface RetractInternalNoteOut {
  outcome: 'deleted' | 'gone'
  turn_id: string
  desk_ticket_id: string
  desk_comment_id: string
  deleted_at: string
  replayed: boolean
}

export interface EditInternalNoteOut {
  turn_id: string
  desk_ticket_id: string
  desk_comment_id: string
  body_text: string
  edited_at: string
  replayed: boolean
}

/** GET /cases/{id}/notes/pending: your own notes waiting for a Desk reconnect. */
export interface PendingNoteOut {
  desk_ticket_id: string
  body_text: string
  queued_at?: string | null
}

/** POST /cases/{id}/tickets/{desk_ticket_id}/undo-join (#122). */
export interface UndoJoinOut {
  from_case_id: string
  to_case_id: string
  to_case_number: string
  desk_ticket_id: string
  ticket_number: string
}

/** GET /cases/{id}/crm-search: a CRM account through Conduit (#125). */
export interface CrmAccountOut {
  crm_account_id: string
  name: string
  city?: string | null
  state?: string | null
  phone?: string | null
  website?: string | null
  already_imported: boolean
}

/** GET /cases/{id}/contact-options (#940). */
export interface TicketContactCandidate {
  contact_id: string
  account_id: string
  name: string
  email: string
  phones: string[]
  sources: ('desk' | 'conduit' | 'crm')[]
  match_reasons: string[]
  rank: number
  current: boolean
}

export interface TicketContactOptions {
  state: 'ready' | 'account_required' | 'unreachable' | 'nothing_matched'
  prompt: string
  desk_ticket_id: string
  ticket_number: string
  account_id?: string | null
  account_name?: string | null
  current_contact_id?: string | null
  caller_name?: string | null
  caller_number?: string | null
  candidates: TicketContactCandidate[]
  sources: {
    source: 'desk' | 'conduit' | 'crm'
    state: 'ok' | 'local' | 'unreachable' | 'feature_off' | 'nothing_matched'
    detail?: string | null
  }[]
  can_add: boolean
}

export interface AddTicketContactIn {
  email: string
  desk_account_id: string
  first_name?: string | null
  last_name?: string | null
  customer_display?: string | null
}

export interface AddTicketContactOut {
  desk_contact_id: string
  created: boolean
  ticket_linked: boolean
  link_error?: string | null
  email: string
  desk_account_id: string
}

/** GET/POST /cases/{id}/presence (#126). */
export interface PresenceView {
  user_id: string
  name: string
  kind: 'viewing' | 'drafting'
  thread_id?: string | null
  thread_audience?: string | null
  thread_label?: string | null
  seen_at: string
  is_me: boolean
}

/** POST /obligations (#147, #583). */
export interface ObligationIn {
  case_id: string
  kind:
    | 'contractor_invoice'
    | 'payment_to_contractor'
    | 'bill_customer'
    | 'follow_up'
    | 'quote_to_send'
    | 'doc_writeback'
    | 'rma_return'
    | 'other'
  owed_by: 'us' | 'customer' | 'contractor' | 'vendor'
  owed_by_ref?: string | null
  expects?: string | null
  due_on?: string | null
}

/** GET /cases/{id}/freshness, then (only when stale) the full live thread fetch. */
export type Freshness = { state: 'fresh' } | { state: 'refreshed'; view: CaseView } | { state: 'unknown'; reason?: string | null }

export type ReclassifyAudience = 'customer' | 'contractor' | 'vendor' | 'internal'

export interface PaneSplitIn {
  source_thread_id: string
  scope: 'party' | 'turn'
  party_email?: string | null
  turn_ids: string[]
  audience: 'customer' | 'contractor' | 'vendor' | 'internal'
  party_ref?: string | null
  display_name?: string | null
  work_order_id?: string | null
  target_thread_id?: string | null
}

export interface PaneUndoOut {
  ok: boolean
  action_id: string
  restored_turn_count?: number | null
  removed_party_routes?: number | null
}

export interface TurnCaseMoveIn {
  turn_id: string
  target_case_id?: string | null
  title?: string | null
}

export interface TurnCaseMoveOut {
  ok: boolean
  action_id: string
  source_case_id: string
  target_case_id: string
  target_case_number: string
  turn_id: string
  desk_ticket_id: string
  mode: 'ticket' | 'message_copy'
  undone?: boolean
}

/** openapi: SendState */
export interface SendState {
  send_box: SendBox
  audience: string
  reason?: string | null
  target_ticket_id?: string | null
  target_ticket_number?: string | null
  visibility?: 'public_customer' | 'public_external' | 'none'
  signature_html?: string | null
  quote_default?: boolean
  customer_conversation_unconfirmed?: boolean
  can_confirm_customer_conversation?: boolean
  /** #1107 alert, robot, or unconfirmed voicemail: open a new customer ticket instead of replying here. */
  can_email_customer?: boolean
  email_customer_subject?: string | null
  email_customer_to?: string | null
  email_customer_contact_name?: string | null
  email_customer_contact_id?: string | null
  email_customer_account_id?: string | null
}

export interface EmailCustomerIn {
  thread_id: string
  to: string
  contact_name?: string | null
  contact_id?: string | null
  account_id?: string | null
  subject?: string | null
  body_html: string
  greeting_name?: string | null
  idempotency_key: string
  acknowledged_warnings?: string[]
}

export interface EmailCustomerOut {
  ok: boolean
  case_id: string
  thread_id: string
  desk_ticket_id: string
  ticket_number: string
  subject: string
}

export interface CustomerConversationConfirmOut {
  outcome: 'promoted' | 'already_promoted'
  reason: 'customer_contact' | 'staff_reply' | 'human_confirm'
  desk_ticket_id: string
  ticket_number: string
}

/** GET /snippets (issue #471). */
export interface SnippetTemplate {
  id: string
  key: string
  title: string
  body_html: string
  body_text: string
  audience: 'customer' | 'contractor' | 'internal'
  tags: string[]
  version: number
  updated_at: string
  active: boolean
}

export type DeskTokenState = 'connected' | 'refresh_failing' | 'not_connected'
export type Role = 'manager' | 'cover' | 'tech' | 'billing'

/** openapi: MeOut (the fields this slice reads). */
export interface MeOut {
  id: string
  name: string
  email: string
  role: Role
  ui_mode: 'full' | 'simple'
  desk_token: DeskTokenState
  can_write_desk: boolean
  is_superadmin?: boolean
  csrf_token: string
}

export type RemovalReason = 'spam' | 'test' | 'duplicate' | 'created_by_mistake'

export interface RemoveCaseInput {
  reason: RemovalReason
  duplicate_of_case_id?: string | null
  delete_desk_tickets: boolean
}

export interface RemovalFlagInput {
  reason: Exclude<RemovalReason, 'created_by_mistake'>
  duplicate_of_case_id?: string | null
  note?: string | null
}

export interface RemoveCaseOutput {
  id: string
  number: string
  title: string
  removed_at: string
  reason: RemovalReason
  desk_tickets: { desk_ticket_id: string; ticket_number: string; deleted: boolean; detail?: string | null }[]
}

export interface RestoreDeskOutput {
  restored: string[]
  failed: string[]
}

export type TimeKind = 'work' | 'travel'

export type TimeSource =
  | 'manual'
  | 'send_prompt'
  | 'close_prompt'
  | 'work_handoff'
  | 'capture'
  | 'packet'
  | 'mobile'
  | 'estimate_accepted'
  | 'desk_auto_counted'

/** openapi: TimeEntryIn */
export interface TimeEntryIn {
  case_id: string
  /** Required by Desk-backed cases; absent when the native case itself receives the time entry. */
  desk_ticket_id?: string
  minutes: number
  billable: boolean
  note?: string | null
  no_time: boolean
  no_time_reason?: string | null
  prompt_ref?: string | null
  source: TimeSource
  /** Work is ordinary labour; travel is priced separately by the billing packet (#793). */
  kind: TimeKind
  /** Phone / IT / MACD the person chose (round 13). */
  domain?: TimeDomain | null
  /** #256: manager/cover attribution target (one of these, never both). */
  user_id?: string | null
  owner_desk_agent_id?: string | null
  /** #186: the tracked seconds this entry accounts for (taken off the case's unlogged time; omitted clears it). */
  tracked_seconds?: number | null
}

/** openapi: TimeEntryOut (the fields this slice reads). Cost fields arrive only for manager/billing. */
export interface TimeEntryOut {
  id: string
  minutes: number
  billable: boolean
  is_no_time: boolean
  no_time_reason?: string | null
  desk_sync_state: 'synced' | 'pending_token' | 'failed' | 'not_applicable'
  is_desk_auto?: boolean
  source: TimeSource
  /** Transitional optionality keeps pre-#793 fixture adapters valid; the HTTP adapter normalizes absence to work. */
  kind?: TimeKind
  user?: { id: string; name: string } | null
  owner_name?: string | null
  entered_by_name?: string | null
  note?: string | null
  executed_at?: string
  subject_kind?: 'tech' | 'contractor'
  cost_amount?: string | null
  domain?: TimeDomain | null
  billable_basis?: string | null
  /** Desk AUTO timer id. A mirrored AUTO row carries its own Desk id; a counted allocation points back at it. */
  source_desk_time_entry_id?: string | null
  /** The mirrored Desk row's own id, when the review id was not projected onto source_desk_time_entry_id (#1185). */
  desk_time_entry_id?: string | null
  desk_auto_review_state?: 'noise' | 'pending' | 'counted' | 'ignored' | null
  desk_auto_actionable?: boolean
}

/** openapi: SearchHit */
export interface SearchHit {
  kind: 'case' | 'ticket' | 'customer' | 'contractor' | 'person' | 'knowledge' | 'account' | 'contact'
  id: string
  label: string
  sublabel?: string | null
  href: string
}

// ---------------------------------------------------------------------------------------------
// View models. The screen renders these; adapters build them from the contract (http) or from
// src/fixtures/cases.ts (fixture mode).

/** A Desk attachment on a turn (`Turn.files`, #173): the id lets "Attach to Conduit" fetch it (#164). */
export type CaseAttachment = TurnFile

export type CaseTurn = Turn & {
  /** #1386 provider receipt state for a native outbound turn. */
  deliveryState?: DeliveryState
}

export interface CaseStatusDef {
  /** Stable backend value written by the status control. */
  name: string
  /** Human label supplied by the backend. */
  label: string
  /** Backend-neutral family used by the existing grouped status picker. */
  status_type: 'open' | 'on_hold' | 'closed'
}

export interface ThreadView extends Thread {
  turns: CaseTurn[]
  /** The contract's thread uuid (POST /send, /compose/check). */
  uuid: string
  /** True when composer.draftHtml is Riley's staged draft (send passes its fingerprint if unchanged). */
  rileyDraft?: boolean
  /** Small text after the tab label ("customer" on a second customer-side thread). */
  sublabel?: string
  /** First name the composer greets, for the addressee check. */
  greetingName?: string
  /** contractor | vendor for a third-party pane (the web Audience folds vendor into contractor). */
  partyAudience?: 'contractor' | 'vendor'
  /** Conduit contractor id, vendor id, or a customer-side person's address. */
  partyRef?: string
  /** An external-conversation pane (#90): its own Desk ticket; never a Move-to target. */
  external?: boolean
  /** A live #412 manual split/merge target. It must not be coalesced with the ordinary Customer pane. */
  manualOverride?: boolean
  /** Latest active split/merge action for this pane; exposes the durable local Undo control after a refresh. */
  manualActionId?: string
  /** Optional WO/engagement selected when the pane was split out. */
  workOrderId?: string
  /** That conversation's Desk ticket id (#586 close). */
  deskTicketId?: string
  /** That ticket's Desk number. */
  ticketNumber?: string
  /** Server-checked #602 eligibility: no ticket, turns, work order or open obligation. */
  canRemove?: boolean
  /** Writers, To recipients and CC recipients on this conversation; identity decorates a party, never a pane. */
  people?: {
    email: string
    name?: string
    partyKind?: 'external' | 'contractor'
    partyRef?: string
    partyName?: string
    hasWorkOrder?: boolean
    /** Desk ticket contact. Sorted first on the strip (#616). */
    ticketContact?: boolean
    /** Desk contact id for the contact page, when this address is that contact (#548). */
    contactId?: string
  }[]
}

export interface CaseBrand {
  slug: string
  customerFacingName: string
  supportFromAddress?: string | null
  ourDomains: string[]
}

export interface CaseView extends Omit<CaseDetail, 'threads' | 'context' | 'workOrders' | 'obligations' | 'time'> {
  /** The contract's case uuid. `id` stays the human number (C-0412) for routes and keys. */
  uuid: string
  /** Ticket engine for this case. Missing legacy API values are adapted to `desk`. */
  backend: Backend
  /** Patchbay's status for a native case. Desk cases leave this absent. */
  nativeStatus?: NativeStatus
  /** Statuses returned by GET /cases/{id}; preferred over Desk's update-form list when present. */
  statusVocabulary?: CaseStatusDef[]
  /** Customer-facing identity and mail domains selected by case formation. Null means unresolved. */
  brand: CaseBrand | null
  /** #928 exact newest customer turn and the lane decision a person may override from the case menu. */
  latestCustomerTurnId?: string
  /** #1086 Desk thread of that turn. A live overlay of the same message uses a different turn id. */
  latestCustomerDeskThreadId?: string
  replyClassification?: 'needs_reply' | 'no_reply_needed' | 'waiting_on_them'
  replyClassificationSource?: 'human' | 'riley' | 'deterministic' | 'default'
  waitingOnParty?: string
  waitingOnWhat?: string
  /** The Desk ticket a reply/time entry lands on. Native cases have no Desk identity. */
  deskTicketId?: string
  /** Primary/service ticket's Desk status name. Absent when the mirror has none. */
  deskStatus?: string
  threads: ThreadView[]
  /** When the mirror copy shown was last received ("as of 10:14"). */
  asOf?: string
  /** Where the turns came from: the mirror (served first) or the live Desk fetch. */
  source?: 'mirror' | 'live'
  /** Conduit customer id (dispatch context, credentials). */
  conduitCustomerId?: string
  /** Other customer links that can be selected without inventing a display name. */
  deskAccountId?: string
  /** Primary ticket's Desk contact id (#548). */
  contactId?: string
  crmAccountId?: string
  /** Staff-managed case labels (the legacy pilot tag is migrated into this list). */
  tags: string[]
  /** Conduit phone system id, when the case knows its system ("Attach to Conduit" can file on it). */
  conduitSystemId?: string
  /** Turns ingest could not classify (Conduit identify unavailable): shown greyed in Customer, never replied to. */
  unclassified?: UnclassifiedTurnView[]
  /** Set when this case is a merge tombstone: the surviving case's number (the route redirects there). */
  mergedInto?: string
  /** The header only renders Riley's structured headline; customer-text fallbacks are never summaries (#382). */
  symptomSource?: 'riley'
  /** The fuller "First: … Newest: …" wording for the dispatch drawer; the header never shows it as one line. */
  symptomFull?: string
  /** The first and newest customer turns, cleaned, for the header's More (newest only when it differs). */
  symptomFirst?: string
  symptomNewest?: string
  /** Riley's latest whole-case summary (#328); the header headline and More panel are both sourced from this object. */
  caseSummary?: {
    headline: string
    problem: string
    tried: string[]
    status: string
    waitingOn: string
    next: string
    asOf: string
  }
  triageNeedsHuman?: string
  triageSafety?: string
  triageCategory?: string
  /** The case's coverage lines (GET /cases/{id}), for the header's meta row. Absent: say nothing, never "not covered". */
  coverage?: CoverageView[]
  /** Every still-linked Desk ticket is in the Recycle Bin or no longer exists. */
  allTicketsDeletedInDesk: boolean
  /** Patchbay soft-removal is reversible and preserved in the audit/event spine. */
  removedAt?: string
  removalReason?: RemovalReason
  duplicateOfCaseId?: string
}

export interface UnclassifiedTurnView {
  /** Turn uuid (POST /turns/{id}/reclassify). */
  id: string
  author: { name: string; email?: string }
  at: string
  body: string
  direction: 'inbound' | 'outbound' | 'note'
  /** Desk @mention ids in `body` resolved to names (#113). */
  mentions?: Record<string, string>
}

export interface CoverageView {
  domain: 'Phone' | 'IT' | 'MACD'
  state: string
  basis: string
}

/** Context rail: Conduit customer + Riley's /context snapshot. */
export interface ContextView {
  coverage: CoverageView[]
  remotePath: 'remote' | 'unconfirmed' | 'none' | 'unknown'
  remoteBasis?: string
  site?: string
  systems: string[]
  handling?: string
  credentials: number | null
  lastKnown: { source: string; state: string; at: string; provenance?: string }[]
  builtAt?: string
  stale?: boolean
  /** Riley answered but has no saved context for this ticket yet (`state: no_context`); not an outage. */
  noContext?: boolean
  /** Riley's /context could not be read. Coverage (from Conduit) still shows; Riley's parts are unknown. */
  rileyUnreachable?: boolean
}

export interface WorkItemView {
  id: string
  kind: string
  title: string
  status: string
  due?: string
  checklist?: { done: number; total: number }
}

export interface RelatedView {
  /** Patchbay case number (C-00107): the link target. */
  number: string
  title: string
  state: string
  ticketNumber?: string
  deskTicketId?: string
  /** #879 group: same customer (default), same site, or only the same management-company domain. */
  scope?: 'customer' | 'site' | 'management'
  scopeLabel?: string
}

export interface WorkOrderView {
  /** The native work order id (opens its drawer on the case, #433; #273). */
  id?: string
  /** Conduit contractor id for a contractor WO (portal access per firm). */
  contractorId?: string
  number: string
  status: WorkOrderStatus
  contractor: string
  window: string
  /** "hours: from invoice" when the visit has a start and no known duration (#580). */
  hours?: string
  nte: string
  tasks: string
  /** "invoice 26207" | "no invoice yet" (contractor WOs; each WO carries its own) */
  invoices?: string
}

export interface ObligationView {
  id: string
  kind: string
  /** Who owes it, as a human label ("us", "customer"; never a raw id). */
  owedBy: string
  /** The other party by name (the firm a payment is owed to, the firm an invoice is owed by), when it resolved. */
  party?: string
  /** "CAD 412.50" (money roles only; absent otherwise). */
  amount?: string
  expects: string
  due: string
}

export interface TimeRowView {
  id: string
  who: string
  minutes: number
  billable: boolean
  source: string
  /** Older mirrored/fixture rows predate #793; treat an omitted kind as work at the view boundary. */
  kind?: TimeKind
  auto?: boolean
  sourceDeskTimeEntryId?: string
  deskAutoReviewState?: 'noise' | 'pending' | 'counted' | 'ignored'
  deskAutoActionable?: boolean
  noTime?: string
  pending?: boolean
  /** #1186: signed-in person who entered delegated time. */
  enteredBy?: string
  /** #726: the time entry's description. The Internal thread shows it in order; the sidebar shows its first line. */
  note?: string
  /** ISO instant the work was done (Desk `executedTime`); orders the entry among the Internal turns. */
  at?: string
}

export type DeskTimeMirrorState = 'cached' | 'ok' | 'not_connected' | 'unreachable'

/** GET /cases/{id}/time plus the `X-Desk-Time` mirror state carried by that response. */
export interface TimeReviewView {
  rows: TimeRowView[]
  mirrorState: DeskTimeMirrorState
}

export interface DeskAutoCountIn {
  work_minutes: number
  travel_minutes: number
  domain?: TimeDomain
  billable?: boolean
  note?: string
}

export interface DeskAutoReviewOut {
  desk_time_entry_id: string
  review_state: 'counted' | 'ignored'
  allocations: TimeEntryOut[]
}

/** A lookup that could not be made. Rendered as "unreachable", never as "none" (hard rule 10). */
export class UnreachableError extends Error {
  constructor(
    readonly source: string,
    detail?: string,
  ) {
    super(detail ? `${source} unreachable: ${detail}` : `${source} unreachable`)
    this.name = 'UnreachableError'
  }
}

/** A Conduit capability gate answered 404. This is a configured-off state, not an outage or an empty list. */
export class FeatureOffError extends Error {
  constructor(readonly source: string) {
    super(`${source} feature off`)
    this.name = 'FeatureOffError'
  }
}

/** A non-2xx answer from the API, with its error code when the body had one. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly detail?: unknown,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

/** openapi: CardOut, the fields the delete-draft confirm reads. */
export interface DraftCardOut {
  id: string
  kind: string
  title?: string | null
  impact_text: string
  audience: string
  executor: 'riley' | 'patchbay'
  state: 'proposed' | 'confirmed' | 'edited_confirmed' | 'dismissed' | 'expired' | 'failed'
  can_confirm: boolean
  result?: {
    ok?: boolean
    outcome?: string
    code?: string
    detail?: string
    path?: string
    [key: string]: unknown
  } | null
  payload?: Record<string, unknown>
}

export interface DeleteDraftIn {
  desk_ticket_id: string
  desk_thread_id: string
  author_name?: string | null
}

export interface DiscardSuggestionOut {
  turn_id: string
  outcome: 'discarded' | 'already_discarded'
}

/** GET /cases/{id}/merge-preview. No Desk call (#265). */
export interface DeskMergeTicket {
  desk_ticket_id: string
  ticket_number: string
  subject: string
}

export interface DeskMergePreview {
  offered: boolean
  default_on: boolean
  reason_code: string
  message: string
  surviving: DeskMergeTicket | null
  absorbed: DeskMergeTicket[]
  skipped: { desk_ticket_id: string; ticket_number: string; why: string }[]
  effects: string
  desk_may_notify: boolean
  department_mismatch: boolean
  warnings: string[]
}

/** The Desk half of a case merge, or POST /cases/{id}/desk-merge. */
export interface DeskMergeResult {
  ok: boolean
  skipped?: boolean
  replayed?: boolean
  code?: string | null
  message: string
  retry?: boolean
  surviving_desk_ticket_id?: string | null
  surviving_case_id?: string
  merged_ticket_ids?: string[]
}

export interface PatchbayDraftSaveIn {
  thread_id: string
  body_html: string
  to?: string[] | null
  cc?: string[] | null
  draft_intent?: 'close' | 'ask' | 'update' | 'resolve' | null
  /** #511: the live Riley suggestion the composer adopted; recorded so its card hides while this draft is live. */
  adopted_from_turn_id?: string | null
}

export interface PatchbayDraftSaveOut {
  turn_id: string | null
  source_version?: string | null
  changed: boolean
  also_in_desk: boolean
}

export interface PatchbayDraftPushOut {
  turn_id: string
  desk_thread_id: string
  outcome: 'created' | 'updated'
}

export interface PatchbayDraftDiscardOut {
  turn_id: string
  outcome: 'discarded' | 'already_discarded'
  desk_deleted: boolean
}

export interface NativeChatDraftDiscardOut {
  turn_id: string
  outcome: 'discarded' | 'already_discarded'
}

/** GET /cases/{id}/candidates: a ticket or case to link/merge with this one (#100). */
export interface CandidateView {
  kind: 'ticket' | 'case'
  id: string
  label: string
  sublabel?: string | null
  ticket_number?: string | null
  case_id?: string | null
  case_number?: string | null
  same_customer: boolean
  closed: boolean
}

/** A contractor directory firm for the "This is a contractor…" and "Move to… new contractor" pickers. */
export interface FirmOption {
  id: string
  conduit_contractor_id: string
  name: string
  status: string
  people: { full_name: string; email?: string | null }[]
}

export interface VendorOption {
  id: string
  name: string
  emails: string[]
}

/** POST /cases/{id}/people/mark-contractor */
export interface MarkContractorIn {
  email: string
  person_name?: string | null
  firm_id?: string | null
  new_firm_name?: string | null
  add_domain?: boolean
}
